import React, { useState } from 'react';
import { useAnafStatus } from '../hooks/useAnafStatus';
import AnafOAuthSetup from './AnafOAuthSetup';

/**
 * ANAF Status Button Component
 *
 * Displays the current ANAF authentication status with color-coded visual indicators.
 * Clicking the button opens a modal for authentication/re-authentication.
 *
 * Color States:
 * - Red (#dc2626): Not authenticated or expired
 * - Yellow (#f59e0b): Expiring soon (< 7 days)
 * - Green (#059669): Authenticated and valid
 * - Gray (#a69e8e): Loading/checking status
 */
export default function AnafStatusButton() {
  const anafStatus = useAnafStatus();
  const [showModal, setShowModal] = useState(false);

  // Determine button color and text based on status
  const getButtonStyle = () => {
    if (anafStatus.loading) {
      return {
        backgroundColor: '#a69e8e',
        color: '#ffffff',
        text: 'ANAF: Se verifică...'
      };
    }

    if (anafStatus.error) {
      return {
        backgroundColor: '#dc2626',
        color: '#ffffff',
        text: 'ANAF: Eroare'
      };
    }

    if (!anafStatus.hasToken || !anafStatus.isActive || anafStatus.isExpired) {
      return {
        backgroundColor: '#dc2626',
        color: '#ffffff',
        text: 'ANAF: Neautentificat'
      };
    }

    if (anafStatus.isExpiringSoon) {
      return {
        backgroundColor: '#f59e0b',
        color: '#ffffff',
        text: 'ANAF: Expiră curând'
      };
    }

    // Valid and active
    return {
      backgroundColor: '#059669',
      color: '#ffffff',
      text: 'ANAF: Autentificat'
    };
  };

  const buttonStyle = getButtonStyle();

  return (
    <>
      {/* Status Button */}
      <button
        onClick={() => setShowModal(true)}
        style={{
          padding: '0.5rem 0.75rem',
          backgroundColor: buttonStyle.backgroundColor,
          color: buttonStyle.color,
          border: 'none',
          borderRadius: '0.375rem',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.15s ease-in-out',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.85';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
        title={
          anafStatus.loading ? 'Se verifică statusul autentificării...' :
          anafStatus.error ? `Eroare: ${anafStatus.error}` :
          !anafStatus.hasToken ? 'Click pentru autentificare ANAF' :
          anafStatus.isExpired ? 'Token expirat - click pentru reautentificare' :
          anafStatus.isExpiringSoon ? `Expiră în ${anafStatus.daysUntilExpiry} zile` :
          `Valid încă ${anafStatus.daysUntilExpiry} zile - Click pentru detalii`
        }
      >
        {buttonStyle.text}
      </button>

      {/* Modal Dialog */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#fdfcf9',
              borderRadius: '0.5rem',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1.5rem',
                borderBottom: '1px solid #e2ded3'
              }}
            >
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#262218' }}>
                Configurare ANAF e-Factura
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#746c5a',
                  padding: '0.25rem',
                  lineHeight: 1
                }}
                title="Închide"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem' }}>
              <AnafOAuthSetup onAuthSuccess={() => {
                anafStatus.refresh();
                setShowModal(false);
              }} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
